// Static shortcut map for the KeyboardShortcutsController.
//
// Pure data structure with no bus dependency: each entry maps a keyboard
// combo to a UiAction type discriminator. Dynamic payload data (selection
// snapshot for `object/delete`, world coords for `clipboard/paste`) is
// resolved at dispatch time by the controller in T5.3.
//
// Source: PRD-C TR6 shortcut table.

import type { UiAction } from '../bus/ui-action.events.js';

/** Single keyboard binding mapping a key combination to a UiAction type. */
export interface ShortcutDefinition {
    /** KeyboardEvent.key value (e.g. 'z', 'Delete', 'Escape'). Case-insensitive match for letters. */
    readonly key: string;
    /** Requires Ctrl (or Meta on macOS) modifier. Defaults to false. */
    readonly ctrl?: boolean;
    /** Requires Shift modifier. Defaults to false (matches only when Shift is NOT held). */
    readonly shift?: boolean;
    /** UiAction discriminator the controller dispatches when this shortcut fires. */
    readonly actionType: UiAction['type'];
}

/**
 * Default shortcut bindings per PRD-C TR6.
 *
 * Order matters for matchers that scan top-to-bottom: more specific bindings
 * (Ctrl+Shift+Z) must precede broader ones (Ctrl+Z) so the modifier fires
 * the correct action.
 */
export const KEYBOARD_SHORTCUTS: readonly ShortcutDefinition[] = [
    { key: 'z', ctrl: true, shift: true, actionType: 'history/redo' },
    { key: 'z', ctrl: true, actionType: 'history/undo' },
    { key: 'y', ctrl: true, actionType: 'history/redo' },
    { key: 'c', ctrl: true, actionType: 'clipboard/copy' },
    { key: 'x', ctrl: true, actionType: 'clipboard/cut' },
    // Anke, 2026-08-15 — Bugreport: Ctrl+A was unhandled here, so the browser's
    // native "select all page text" fired unopposed (highlighting toolbar
    // labels etc., not just board objects). isEditableTarget (checked before
    // any shortcut lookup, see the controller's #handler) still lets native
    // select-all through while the user is typing inside a textbox — this
    // entry only fires with the canvas itself as the interaction context.
    { key: 'a', ctrl: true, actionType: 'selection/select-all' },
    // Kein 'v'-Eintrag: Strg+V wird bewusst NICHT hier abgefangen. Würde dieser
    // Controller den Tastendruck matchen, riefe er preventDefault() auf — und
    // damit schaltet der Browser sein natives `paste`-Event ab, über das
    // Screenshots aus der System-Zwischenablage hereinkommen (ImagePasteHandler).
    // Einfügen läuft deshalb komplett über das native paste-Event: liegt ein
    // Bild in der Zwischenablage → Bild, sonst → interne Board-Objekte.
    { key: 'Delete', actionType: 'object/delete' },
    { key: 'Backspace', actionType: 'object/delete' },
    { key: 'Escape', actionType: 'selection/clear' },
    // Arrow-key nudge — 1 document px, or 10 px with Shift (resolved by the
    // controller from the event's shiftKey). Both Shift and non-Shift bindings
    // map to the same `object/nudge` action; the #findShortcut matcher needs
    // an explicit `shift: true` entry because it matches the modifier exactly.
    { key: 'ArrowUp', actionType: 'object/nudge' },
    { key: 'ArrowUp', shift: true, actionType: 'object/nudge' },
    { key: 'ArrowDown', actionType: 'object/nudge' },
    { key: 'ArrowDown', shift: true, actionType: 'object/nudge' },
    { key: 'ArrowLeft', actionType: 'object/nudge' },
    { key: 'ArrowLeft', shift: true, actionType: 'object/nudge' },
    { key: 'ArrowRight', actionType: 'object/nudge' },
    { key: 'ArrowRight', shift: true, actionType: 'object/nudge' },
] as const;
